import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ApiResponse } from '../models/api-response.model';
import { ApiErrorResponse } from '../models/api-error-response.model';
import { environment } from '@environment/environment';
import { DebugHelper } from '@shared/helpers/debug.helper';

const unknownErrorMessage =
    'Ocorreu um erro inesperado, por favor, tente mais tarde';

// HTTP request bodies cross the network boundary; the shape is defined per
// endpoint contract, not here. `any` on body is permitted under constitution
// v1.1.0 Principle I (inline justification) because the wrapper is generic.
// eslint-disable-line
// @typescript-eslint/no-explicit-any
type ApiRequestBody = any;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private _httpClient = inject(HttpClient);

  get<T>(url: string): Observable<T | null> {
    return this._httpClient
               .get<ApiResponse<T>>(url)
               .pipe(
                   map((response) => this._getResponseData<T>(response)),
                   catchError((errorResponse: ApiErrorResponse) => {
                     throw this._errorHandler({
                       method: 'GET',
                       errorResponse: errorResponse.error,
                     });
                   }),
               );
  }

  post<T>({ url, body }: { url: string; body: ApiRequestBody }): Observable<T | null> {
    return this._httpClient
               .post<ApiResponse<T>>(url, body)
               .pipe(
                   tap((response) => this._showSuccessToast(response.message)),
                   map((response) => this._getResponseData<T>(response)),
                   catchError((errorResponse: ApiErrorResponse) => {
                     throw this._errorHandler({
                       method: 'POST',
                       errorResponse: errorResponse.error,
                     });
                   }),
               );
  }

  put<T>({ url, body }: { url: string; body: ApiRequestBody }): Observable<T | null> {
    return this._httpClient
               .put<ApiResponse<T>>(url, body)
               .pipe(
                   tap((response) => this._showSuccessToast(response.message)),
                   map((response) => this._getResponseData<T>(response)),
                   catchError((errorResponse: ApiErrorResponse) => {
                     throw this._errorHandler({
                       method: 'PUT',
                       errorResponse: errorResponse.error,
                     });
                   }),
               );
  }

  delete<T>({ url, body }: {
    url: string,
    body?: ApiRequestBody
  }): Observable<T | null> {
    return this._httpClient
               .delete<ApiResponse<T>>(url, { body })
               .pipe(
                   tap((response) => this._showDeleteToast(response.message)),
                   map((response) => this._getResponseData<T>(response)),
                   catchError((errorResponse: ApiErrorResponse) => {
                     throw this._errorHandler({
                       method: 'DELETE',
                       errorResponse: errorResponse.error,
                     });
                   }),
               );
  }

  private _showSuccessToast(_message: string | null): void {
    // wired to ToastService in T020
  }

  private _showDeleteToast(_message: string | null): void {
    // wired to ToastService in T020
  }

  private _getResponseData<T>(response: ApiResponse<T> | null | undefined): T | null {
    if (!response) {
      DebugHelper.log('API response is empty');
      return null;
    }

    if (response.data === null || response.data === undefined) {
      DebugHelper.log('API response has no `data` field');
      return null;
    }

    return response.data;
  }

  private _errorHandler({
    method,
    errorResponse,
  }: {
    method: string;
    errorResponse: ApiResponse<null> | null | undefined;
  }): ApiResponse<null> {
    if (!errorResponse) {
      return { code: 0, message: unknownErrorMessage, data: null };
    }

    if (!environment.production) {
      DebugHelper.log(`API ${ method } Error:`, errorResponse);
    }

    const isUnknownError = errorResponse.message === 'Unknown Error';

    if (isUnknownError) {
      return { ...errorResponse, message: unknownErrorMessage };
    }

    return {
      ...errorResponse,
      message: errorResponse.message || unknownErrorMessage,
    };
  }
}
